import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../api/apiConfig';
import { Button, Input, TextArea, FormField, Alert } from '../ui';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';

interface Person {
  id: number;
  name: string;
  relationship?: string;
  notes?: string;
  appreciation?: string;
  triggers?: string;
  last_interaction?: string;
  tasks?: Array<{
    id: number;
    title: string;
    status: string;
    due_date?: string;
  }>;
  projects?: Array<{
    id: number;
    name: string;
    status: string;
  }>;
  goals?: Array<{
    id: number;
    title: string;
    status: string;
    target_date?: string;
  }>;
  journal_entries?: Array<{
    id: number;
    content: string;
    created_at: string;
    emotion?: string;
  }>;
}

interface PersonFormProps {
  isOpen: boolean;
  onClose: () => void;
  onPersonCreated: (person: Person) => void;
  initialPerson?: Person;
  isEditMode?: boolean;
}

const PersonForm: React.FC<PersonFormProps> = ({
  isOpen,
  onClose,
  onPersonCreated,
  initialPerson,
  isEditMode = false,
}) => {
  const [formData, setFormData] = useState<Partial<Person>>({
    name: '',
    relationship: '',
    notes: '',
    appreciation: '',
    triggers: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initialPerson) {
      setFormData({
        name: initialPerson.name,
        relationship: initialPerson.relationship || '',
        notes: initialPerson.notes || '',
        appreciation: initialPerson.appreciation || '',
        triggers: initialPerson.triggers || '',
      });
    }
  }, [initialPerson]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let response;
      if (isEditMode && initialPerson?.id) {
        response = await apiClient.put(`/api/people/${initialPerson.id}/`, formData);
      } else {
        response = await apiClient.post('/api/people/', formData);
      }

      if (response.status >= 200 && response.status < 300) {
        onPersonCreated(response.data);
        onClose();
      } else {
        throw new Error(`Failed to ${isEditMode ? 'update' : 'create'} person (${response.status})`);
      }
    } catch (err: unknown) {
      console.error("Error saving person:", err);
      const errorMsg = err instanceof Error ? err.message : "Failed to save person. Please try again.";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        style={{ position: 'relative', zIndex: 50 }}
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.25)',
            }}
          />
        </Transition.Child>

        <div
          style={{
            position: 'fixed',
            inset: 0,
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              display: 'flex',
              minHeight: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              padding: SPACING[16],
              textAlign: 'center',
            }}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                style={{
                  width: '100%',
                  maxWidth: '32rem',
                  transform: 'none',
                  overflow: 'hidden',
                  borderRadius: '16px',
                  backgroundColor: COLORS.bg.panel,
                  padding: SPACING[24],
                  textAlign: 'left',
                  verticalAlign: 'middle',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                  transition: 'all',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: SPACING[16],
                  }}
                >
                  <Dialog.Title
                    as="h3"
                    style={{
                      fontSize: TYPOGRAPHY.size.lg,
                      fontWeight: TYPOGRAPHY.weight.medium,
                      color: COLORS.text.primary,
                    }}
                  >
                    {isEditMode ? 'Edit Person' : 'Add New Person'}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    style={{
                      color: COLORS.text.tertiary,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <XMarkIcon style={{ width: '24px', height: '24px' }} />
                  </button>
                </div>

                <form
                  onSubmit={handleSubmit}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: SPACING[16],
                  }}
                >
                  {error && (
                    <Alert variant="error">
                      {error}
                    </Alert>
                  )}

                  <FormField label="Name" htmlFor="name" required>
                    <Input
                      id="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </FormField>

                  <FormField label="Relationship" htmlFor="relationship">
                    <Input
                      id="relationship"
                      type="text"
                      value={formData.relationship}
                      onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                    />
                  </FormField>

                  <FormField label="Appreciation" htmlFor="appreciation">
                    <TextArea
                      id="appreciation"
                      rows={2}
                      value={formData.appreciation}
                      onChange={(e) => setFormData({ ...formData, appreciation: e.target.value })}
                    />
                  </FormField>

                  <FormField label="Triggers" htmlFor="triggers">
                    <TextArea
                      id="triggers"
                      rows={2}
                      value={formData.triggers}
                      onChange={(e) => setFormData({ ...formData, triggers: e.target.value })}
                    />
                  </FormField>

                  <FormField label="Notes" htmlFor="notes">
                    <TextArea
                      id="notes"
                      rows={3}
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    />
                  </FormField>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: SPACING[12],
                      paddingTop: SPACING[16],
                    }}
                  >
                    <Button
                      type="button"
                      onClick={onClose}
                      variant="secondary"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      isLoading={loading}
                      loadingText={isEditMode ? 'Saving Changes...' : 'Adding Person...'}
                    >
                      {isEditMode ? 'Save Changes' : 'Add Person'}
                    </Button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default PersonForm;
